<script setup lang="ts">
import { defineProps, ref, watch } from 'vue'
import { useAnnotationPopoverPosition } from 'Modules/composables/useAnnotationPopoverPosition'
import { ImageAnnotation } from 'Modules/types/pages/imageReview/imageAnnotationType'
import { TransformMatrixType } from 'Common/utils/markerArea/types'
import { formatToTwoDecimalPlaces } from 'Modules/utils/displayFilters'
import { AnnotationPopoverPositions, MeasurementStatus } from 'Modules/types/pages/imageReview/enums'
import type { Ref } from 'vue'

interface PropTypes {
  annotations: Array<ImageAnnotation>
  tranformMatrix: Ref<TransformMatrixType>
}
interface PopoverPosition {
  locationX?: number
  locationY?: number
  position?: AnnotationPopoverPositions
}
interface AnnotationPopoverItem {
  position: PopoverPosition
  annotation: ImageAnnotation
}

const props = defineProps<PropTypes>()

// Reactive array to store positions along with the original annotation data
const positions = ref<Array<{ position: PopoverPosition; annotation: ImageAnnotation }>>([])

const showPopover = (item: AnnotationPopoverItem): boolean => {
  return (
    (item.annotation.defectSurfaceArea !== undefined && item.annotation.defectSurfaceAreaUnits !== undefined) ||
    item.annotation.surfaceAreaStatus === MeasurementStatus.Pending
  )
}

const popoverLabel = (item: AnnotationPopoverItem): string | undefined => {
  const isPending = item.annotation.surfaceAreaStatus === MeasurementStatus.Pending
  const hasLabelDetails = item.annotation.defectSurfaceArea && item.annotation.defectSurfaceAreaUnits

  if (hasLabelDetails && !isPending)
    return `${formatToTwoDecimalPlaces(item.annotation.defectSurfaceArea)}${item.annotation.defectSurfaceAreaUnits}`

  if (isPending) return `Calculating Surface Area...`

  return
}

watch(
  [() => props.annotations, () => props.tranformMatrix],
  () => {
    // Ensure props.annotations is a valid array of objects before processing
    if (Array.isArray(props.annotations) && props.annotations.length > 0) {
      positions.value = [] // Clear previous positions

      // Loop through each annotation object and calculate the position
      props.annotations.forEach((annotation, index) => {
        if (annotation) {
          const positionRef = useAnnotationPopoverPosition(annotation, props.tranformMatrix.value)
          positions.value.push({
            position: positionRef,
            annotation: annotation,
          })
        } else {
          console.warn(`Invalid annotation at index ${index}`)
        }
      })
    } else {
      console.warn('props.annotations is not a valid array of objects or is empty')
      positions.value = [] // Reset positions if annotations are invalid or empty
    }
  },
  {
    deep: true,
  }
)
</script>
<template>
  <div
    v-for="(item, index) in positions"
    :key="index"
    class="annotation-popover absolute z-10"
    :style="{
      top: item.position.locationY + 'px',
      left: item.position.locationX + 'px',
    }"
  >
    <template v-if="showPopover(item)">
      <div
        class="absolute top-0 left-0 z-10 w-auto px-4 py-1 text-white bg-black border border-black shadow-md bg-opacity-50 whitespace-nowrap"
        :style="{ transform: 'translate(-50%, -150%)' }"
      >
        <span v-if="showPopover(item)">
          {{ popoverLabel(item) }}
        </span>
      </div>
    </template>
  </div>
</template>
